
/**
 * Attempts to extract a procedure identifier from TED notice data
 * The procedure identifier is different from the notice identifier and links related notices together
 */
export const extractProcedureIdentifier = (tenderData: any): string | null => {
  // Direct fields that might contain the procedure identifier
  const possibleFields = [
    "procedure-identifier",     // Primary field
    "BT-701-notice",           // Procedure reference field 
    "BT-1251-Procedure",
    "BT-1251-Part",
    "BT-1251-Lot"
  ];
  
  // Pattern matching a UUID format
  const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
  
  // First check the direct fields
  for (const field of possibleFields) {
    if (tenderData[field]) {
      const fieldValue = tenderData[field];
      
      // Handle string values
      if (typeof fieldValue === 'string') {
        if (uuidPattern.test(fieldValue)) {
          return fieldValue;
        }
      }
      
      // Handle arrays
      if (Array.isArray(fieldValue)) {
        for (const item of fieldValue) {
          if (typeof item === 'string' && uuidPattern.test(item)) {
            return item;
          }
        }
      }
      
      // Handle multilingual objects
      if (typeof fieldValue === 'object' && fieldValue !== null) {
        const languages = Object.keys(fieldValue);
        for (const lang of languages) {
          const langValue = fieldValue[lang];
          if (typeof langValue === 'string' && uuidPattern.test(langValue)) {
            return langValue;
          }
          if (Array.isArray(langValue)) {
            for (const item of langValue) {
              if (typeof item === 'string' && uuidPattern.test(item)) {
                return item;
              }
            }
          }
        }
      }
    }
  }
  
  // Special case: Check FT field which often contains the procedure identifier in the text
  if (tenderData.FT) {
    // First, handle array case
    if (Array.isArray(tenderData.FT)) {
      for (const item of tenderData.FT) {
        // Look in each language version
        if (typeof item === 'object') {
          const languages = Object.keys(item);
          for (const lang of languages) {
            const textContent = item[lang];
            if (typeof textContent === 'string') {
              // Look for pattern: "Toldboden 2\nnone\n{UUID}" which has been observed in the data
              const toldbMatch = textContent.match(/Toldboden\s+2\s*\n+none\s*\n+([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
              if (toldbMatch && toldbMatch[1]) {
                return toldbMatch[1];
              }
              
              // General search for UUIDs in text - but not at the very end, which is typically the notice ID
              const matches = textContent.match(uuidPattern);
              if (matches && matches.length > 0) {
                // Get all UUIDs in the text
                const allMatches = [...textContent.matchAll(new RegExp(uuidPattern, 'g'))];
                
                // If there's only one match and it's not at the very end, return it
                if (allMatches.length === 1) {
                  const matchPos = textContent.indexOf(matches[0]);
                  if (matchPos < textContent.length - 40) {
                    return matches[0];
                  }
                }
                
                // If there are multiple matches, use the one that's not at the very end
                if (allMatches.length > 1) {
                  // The last UUID is typically the notice ID, so return the second-to-last one
                  return allMatches[allMatches.length - 2][0];
                }
              }
            }
          }
        } else if (typeof item === 'string' && uuidPattern.test(item)) {
          return item;
        }
      }
    }
    
    // Handle string or object case
    if (typeof tenderData.FT === 'string') {
      const toldbMatch = tenderData.FT.match(/Toldboden\s+2\s*\n+none\s*\n+([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
      if (toldbMatch && toldbMatch[1]) {
        return toldbMatch[1];
      }
      
      const matches = tenderData.FT.match(uuidPattern);
      if (matches) return matches[0];
    } else if (typeof tenderData.FT === 'object' && tenderData.FT !== null) {
      const languages = Object.keys(tenderData.FT);
      for (const lang of languages) {
        const textContent = tenderData.FT[lang];
        if (typeof textContent === 'string') {
          const toldbMatch = textContent.match(/Toldboden\s+2\s*\n+none\s*\n+([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
          if (toldbMatch && toldbMatch[1]) {
            return toldbMatch[1];
          }
          
          const matches = textContent.match(uuidPattern);
          if (matches) return matches[0];
        }
      }
    }
  }
  
  // Check BT-88-Procedure field which may contain the procedure ID
  if (tenderData["BT-88-Procedure"]) {
    const value = tenderData["BT-88-Procedure"];
    if (typeof value === 'string' && uuidPattern.test(value)) {
      return value;
    }
  }
  
  // If not found in direct fields, scan for values that likely represent procedure identifiers
  // but are not in standard fields (handle with care as this can return the wrong ID)
  const searchForProcedureId = (obj: any): string | null => {
    if (!obj || typeof obj !== 'object') return null;
    
    // Look for fields that typically contain procedure IDs but might have different names
    const likelyProcedureIdFields = [
      "procedure-id", 
      "procurement-id", 
      "procurementId", 
      "procedureId",
      "BT-88-Procedure"
    ];
    
    for (const [key, value] of Object.entries(obj)) {
      // Check if the key is likely to contain a procedure identifier
      if (likelyProcedureIdFields.includes(key) || 
          key.toLowerCase().includes("procedure") || 
          key.toLowerCase().includes("procurement")) {
        if (typeof value === 'string' && uuidPattern.test(value)) {
          return value;
        }
      }
      
      // Process strings that might contain identifiers
      if (typeof value === 'string') {
        // Look for strings with procedure ID prefix
        const prefixedMatch = value.match(/(?:procedure[- _]?id|procurement[- _]?id)[^:]*:\s*([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
        if (prefixedMatch && prefixedMatch[1]) {
          return prefixedMatch[1];
        }
      }
      
      // Recursively check nested objects
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const result = searchForProcedureId(value);
        if (result) return result;
      }
      
      // Check arrays
      if (Array.isArray(value)) {
        for (const item of value) {
          if (typeof item === 'object') {
            const result = searchForProcedureId(item);
            if (result) return result;
          }
        }
      }
    }
    
    return null;
  };
  
  // Try deep search as a last resort
  return searchForProcedureId(tenderData);
};
